import { useMemo, useRef } from "react";
import { type QueryClient, useMutationState, useQueryClient } from "@tanstack/react-query";
import { remiLogger } from "@remi/logging";
import { useEffect } from "@remi/next-ui";
import { isOneOf } from "@remi/ts-utils";
import type { Favorite, FavoriteRecordRef } from "@remi/types/favorite";

import { api } from "~/utils/api";
import { display } from "~/components/ui/sonner";
import { useClearFavoritePlacement } from "~/hooks/sidebar/useClearFavoritePlacement";
import { getPageFavoriteKey, getRecordFavoriteKey } from "./favoritePlacement";

// Only the unified sidebar renders the favorites list, so until it ships the star saves to a list
// nothing shows — every favorite would vanish on save. The whole control stays behind that flag.
export const UNIFIED_SIDEBAR_FEATURE_FLAG = "unified-sidebar";

// Shared scope queues toggles instead of running them in parallel: a star racing an unstar would
// otherwise persist the last write, not the last click.
const FAVORITE_SCOPE_ID = "person-favorite";
const NAV_KEY_FAVORITE_SCOPE_ID = "person-favorite-nav-key";

// Every star and the sidebar read this list; toggles keep it current through setData + invalidate,
// so a mount alone has no reason to refetch.
const FAVORITES_STALE_TIME_MS = 5 * 60 * 1000;

const LIVE_STATUS_REFETCH_MS = 60 * 1000;

const COMMUNICATION_RECORD_WRITES = [
  "updateConversation",
  "inboxBulkResolveConversations",
  "inboxBulkCompleteTasks",
] as const;

/** Refetches favorites after this tab saves a task or resolves/reopens a conversation, singly or in
 *  bulk, so a task's status icon and a conversation's unresolved dot follow without the next poll. */
export const useRefreshFavoritesOnRecordWrites = () => {
  const { favorites } = api.useUtils();
  const processedMutationIds = useRef(new Set<number>());
  const savedMutationIds = useMutationState({
    filters: {
      status: "success",
      predicate: (mutation) => {
        const path: unknown = mutation.options.mutationKey?.[0];
        return (
          Array.isArray(path) &&
          (path[0] === "task" || (path[0] === "communication" && isOneOf(path[1], COMMUNICATION_RECORD_WRITES)))
        );
      },
    },
    select: (mutation) => mutation.mutationId,
  });

  useEffect(() => {
    const unseen = savedMutationIds.filter((id) => !processedMutationIds.current.has(id));
    for (const id of unseen) {
      processedMutationIds.current.add(id);
    }
    if (unseen.length > 0) {
      void favorites.listMine.invalidate();
    }
  }, [savedMutationIds, favorites]);
};

const getFavoriteKey = (ref: FavoriteRecordRef): string => `${ref.type}:${ref.id}`;

/** `live` keeps the list's statuses current; only the sidebar asks for it, not every star. */
export const useEntityFavorites = ({ live = false }: { live?: boolean } = {}) => {
  const { data, isPending } = api.favorites.listMine.useQuery(undefined, {
    staleTime: FAVORITES_STALE_TIME_MS,
    refetchInterval: live ? LIVE_STATUS_REFETCH_MS : undefined,
  });
  const favorites = useMemo(() => data ?? [], [data]);
  const favoritedKeys = useMemo(() => new Set(favorites.map((favorite) => getFavoriteKey(favorite.ref))), [favorites]);

  return {
    favorites,
    isLoaded: !isPending,
    isFavorite: (ref: FavoriteRecordRef) => favoritedKeys.has(getFavoriteKey(ref)),
  };
};

export const useFavoriteNavKeys = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { data, isPending } = api.favorites.listMyFavoriteNavKeyEntries.useQuery(undefined, {
    enabled,
    staleTime: FAVORITES_STALE_TIME_MS,
  });
  const entries = useMemo(() => data ?? [], [data]);
  const navKeys = useMemo(() => entries.map((entry) => entry.navKey), [entries]);
  return { entries, navKeys, isLoaded: !isPending };
};

// A queued toggle's response only knows its own write, so reconciling before the queue drains
// applies a snapshot that predates the pending clicks and reverts them. The count includes the
// settling mutation, which is still 'pending' while its own onSettled runs.
export const isLastInScope = (queryClient: QueryClient, scopeId: string): boolean =>
  queryClient.isMutating({ predicate: (mutation) => mutation.options.scope?.id === scopeId }) <= 1;

/** Toggling needs the record's label to show the new row before the server answers, and the label
 *  is the caller's to supply — the server resolves the authoritative one on the way back. */
export const useToggleFavorite = () => {
  const { favorites } = api.useUtils();
  const queryClient = useQueryClient();
  const clearPlacement = useClearFavoritePlacement();

  const mutation = api.favorites.setFavorite.useMutation({
    scope: { id: FAVORITE_SCOPE_ID },
    // Swaps the finished row in for the optimistic placeholder one round trip before the refetch.
    // An unfavorite, or a record that no longer resolves, answers null and leaves it to the refetch.
    onSuccess(saved) {
      if (saved === null) {
        return;
      }
      const key = getFavoriteKey(saved.ref);
      favorites.listMine.setData(undefined, (current) =>
        current?.map((favorite) => (getFavoriteKey(favorite.ref) === key ? saved : favorite)),
      );
    },
    // Not awaited: mutateAsync resolves only after onSettled, and the reconcile need not hold it.
    onSettled() {
      if (isLastInScope(queryClient, FAVORITE_SCOPE_ID)) {
        void favorites.listMine.invalidate();
      }
    },
  });

  /** Flips the favorite, or sets it to `target` — a no-op when it is already there. */
  return async (ref: FavoriteRecordRef, label: string, target?: boolean) => {
    // Stop any in-flight listMine refetch first, or one landing mid-toggle overwrites the optimistic
    // row and the star snaps back until the mutation settles.
    await favorites.listMine.cancel();
    const key = getFavoriteKey(ref);
    const previous = favorites.listMine.getData() ?? [];
    const isFavorited = previous.some((favorite) => getFavoriteKey(favorite.ref) === key);
    const favorited = target ?? !isFavorited;
    if (favorited === isFavorited) {
      return;
    }
    clearPlacement(getRecordFavoriteKey(ref));
    const without = previous.filter((favorite) => getFavoriteKey(favorite.ref) !== key);

    // The full name is the server's to resolve, so the optimistic row carries the caller's label
    // until the save answers with the finished row.
    const optimistic: Favorite[] = favorited ? [{ ref, label, favoritedAt: new Date() }, ...without] : without;
    favorites.listMine.setData(undefined, optimistic);

    // Per-call mutate callbacks only reach the latest call on a mounted observer, so each save
    // handles its own failure on its own promise. Not awaited: callers act on the optimistic state.
    void mutation.mutateAsync({ ref, favorited }).catch((error: unknown) => {
      remiLogger.error("Favorite save failed", { error, attributes: { favoriteLabel: label } });
      display.error("Could not save that favorite", {
        description: error instanceof Error ? error.message : undefined,
      });
      favorites.listMine.setData(undefined, previous);
    });
  };
};

export const useToggleNavKeyFavorite = () => {
  const { favorites } = api.useUtils();
  const queryClient = useQueryClient();
  const clearPlacement = useClearFavoritePlacement();

  const mutation = api.favorites.setFavoriteNavKey.useMutation({
    scope: { id: NAV_KEY_FAVORITE_SCOPE_ID },
    async onSettled() {
      if (isLastInScope(queryClient, NAV_KEY_FAVORITE_SCOPE_ID)) {
        await favorites.listMyFavoriteNavKeyEntries.invalidate();
      }
    },
  });

  return async (navKey: string, label: string, target?: boolean) => {
    await favorites.listMyFavoriteNavKeyEntries.cancel();
    const previous = favorites.listMyFavoriteNavKeyEntries.getData() ?? [];
    const isFavorited = previous.some((entry) => entry.navKey === navKey);
    const favorited = target ?? !isFavorited;
    if (favorited === isFavorited) {
      return;
    }
    clearPlacement(getPageFavoriteKey(navKey));
    favorites.listMyFavoriteNavKeyEntries.setData(
      undefined,
      favorited
        ? [{ navKey, favoritedAt: new Date() }, ...previous]
        : previous.filter((entry) => entry.navKey !== navKey),
    );

    void mutation.mutateAsync({ navKey, favorited }).catch((error: unknown) => {
      remiLogger.error("Page favorite save failed", { error, attributes: { favoriteLabel: label } });
      display.error("Could not save that favorite", {
        description: error instanceof Error ? error.message : undefined,
      });
      favorites.listMyFavoriteNavKeyEntries.setData(undefined, previous);
    });
  };
};
